/*------------------------------------------------------------------------------
   About      : Permission-aware route guard (RBAC v2)

   Pathless layout route — nest gated routes under it INSIDE the existing
   RouteGuard tree, so the session (and permissions) are guaranteed loaded:

     <Route element={<RequirePermission require={ROUTE_TREE.X.Y.permissions!} />}>
       <Route path={ROUTE_TREE.X.Y.path} element={<SomePage />} />
     </Route>

   Denied: renders the 403 page, or redirects when `redirectTo` is set.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import React                     from 'react'
import { Navigate, Outlet }      from 'react-router'
import { usePermissions }        from 'hooks/usePermissions'
import { PermissionRequirement } from 'framework/access/accessControl'
import ForbiddenComponent        from 'features/root/ForbiddenComponent'

export interface RequirePermissionProps {
  /** A single requirement, or an array where EVERY entry must pass (used to
   *  AND a route's API `permissions` with its registry `uiPageId` check). */
  require     : PermissionRequirement | readonly PermissionRequirement[]
  /** When set, redirect on denial instead of rendering the 403 page */
  redirectTo ?: string
}

export const RequirePermission = ({ require, redirectTo } : RequirePermissionProps) => {

  const { can } = usePermissions()

  const granted = Array.isArray(require) ? require.every(can) : can(require as PermissionRequirement)

  if (granted) return <Outlet />

  return redirectTo ? <Navigate to={redirectTo} replace /> : <ForbiddenComponent />
}
